'use client';

import Link from 'next/link';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { marketplaceExtensions } from '@/config/marketplaceExtensions';
import { MarketplaceSellerTermsReaccept } from '@/platform/legal/MarketplaceSellerTermsReaccept';
import { ConnectNotificationBanner } from './ConnectNotificationBanner';
import { SellerPauseSelling } from './SellerPauseSelling';
import { SellerSetupChecklist } from './SellerSetupChecklist';
import { VendorClaimLinksPanel } from './VendorClaimLinksPanel';
import { VendorOnboarding } from './VendorOnboarding';
import { VendorOrdersPanel } from './VendorOrdersPanel';
import { VendorOverviewPanel } from './VendorOverviewPanel';
import { VendorProductsPanel } from './VendorProductsPanel';
import { VendorShippingPanel } from './VendorShippingPanel';
import { VendorStorePanel } from './VendorStorePanel';
import { getSellerState, getVerificationStatus, type SellerCapabilities, type VendorState } from './api';
import './vendor.css';

export interface SellerConsoleProps {
  sellerUserId: string;
  onCapabilitiesChange?: () => void;
}

export function SellerConsole({ sellerUserId, onCapabilitiesChange }: SellerConsoleProps) {
  const [active, setActive] = useState('overview');
  const [caps, setCaps] = useState<SellerCapabilities | null>(null);
  const [vendorState, setVendorState] = useState<VendorState | null>(null);
  const [showApply, setShowApply] = useState(false);

  const refresh = useCallback(() => {
    return Promise.all([getSellerState(), getVerificationStatus()])
      .then(([seller, verification]) => {
        setCaps(seller);
        setVendorState(verification.state);
        onCapabilitiesChange?.();
      })
      .catch(() => undefined);
  }, [onCapabilitiesChange]);

  useEffect(() => {
    void refresh();
  }, [refresh, sellerUserId]);

  const sellerTabs = useMemo(() => {
    const tabs: { id: string; label: string }[] = [{ id: 'overview', label: 'Overview' }];
    if (caps?.canManageCatalog) {
      tabs.push(
        { id: 'store', label: 'Store' },
        { id: 'products', label: 'Products' },
        { id: 'shipping', label: 'Shipping' },
      );
      if (marketplaceExtensions.showClaimLinksTab) {
        tabs.push({ id: 'claims', label: 'Claim links' });
      }
    } else if (caps?.canListFinds) {
      tabs.push({ id: 'products', label: 'Products' });
    }
    if (caps?.canListFinds || caps?.canManageCatalog) {
      tabs.push({ id: 'orders', label: 'Orders' });
    }
    return tabs;
  }, [caps]);

  const suspended = vendorState?.vendorStatus === 'suspended' || vendorState?.vendorStatus === 'terminated';
  const readOnly = suspended;
  const needsTermsReaccept =
    caps != null &&
    !caps.marketplaceTermsAccepted &&
    (caps.connectOnboardingComplete || caps.vendorEnabled || caps.vendorVerificationStatus === 'verified');

  const showVerificationFlow =
    showApply ||
    caps?.vendorVerificationStatus === 'pending' ||
    caps?.vendorVerificationStatus === 'rejected';

  function renderVendorOnboarding() {
    return (
      <VendorOnboarding
        vendorUserId={sellerUserId}
        onActivated={() => {
          setShowApply(false);
          void refresh();
        }}
        onApplicationChange={() => {
          // Keep apply UI mounted after withdraw (rejected → none) and mid-apply refresh.
          setShowApply(true);
          void refresh();
        }}
      />
    );
  }

  function renderPanel(tabId: string) {
    if (needsTermsReaccept && tabId !== 'overview') {
      return <p className="text-muted">Accept the updated seller terms to manage this section.</p>;
    }

    if (readOnly && (tabId === 'store' || tabId === 'products' || tabId === 'claims' || tabId === 'shipping')) {
      return <p className="text-muted">Editing is disabled while your seller account is {vendorState?.vendorStatus}.</p>;
    }

    switch (tabId) {
      case 'overview':
        if (!caps?.canListFinds && !caps?.canManageCatalog) {
          return (
            <section>
              <h2 className="vendor-console__section-title">Seller setup</h2>
              <p className="vendor-console__hint">
                Accept marketplace terms and connect Stripe payouts to list finds or apply for a full storefront.
              </p>
              <SellerSetupChecklist
                sellerUserId={sellerUserId}
                onReady={() => {
                  void refresh();
                }}
                showStorefrontUpgrade
              />
              {showVerificationFlow && renderVendorOnboarding()}
              {!showVerificationFlow && (
                <p>
                  <button type="button" className="btn btn-ghost" onClick={() => setShowApply(true)}>
                    Apply for storefront
                  </button>
                </p>
              )}
            </section>
          );
        }
        if (caps?.canListFinds && !caps?.canManageCatalog) {
          return (
            <section>
              <h2 className="vendor-console__section-title">Overview</h2>
              <p className="vendor-console__hint">You can list finds from community-approved haul reports.</p>
              <p>
                <Link href="/claims/" className="btn btn-primary">
                  List a find from your haul reports
                </Link>
              </p>
              <p>
                <Link href="/marketplace/sell/">Manage find orders</Link>
              </p>
              {showVerificationFlow ? (
                renderVendorOnboarding()
              ) : (
                <p>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowApply(true)}>
                    Apply for storefront
                  </button>
                </p>
              )}
            </section>
          );
        }
        return <VendorOverviewPanel vendorUserId={sellerUserId} />;
      case 'store':
        return <VendorStorePanel vendorUserId={sellerUserId} readOnly={readOnly} />;
      case 'products':
        if (!caps?.canManageCatalog) {
          return (
            <section>
              <h2 className="vendor-console__section-title">Storefront catalog</h2>
              <p className="text-muted">Admin verification unlocks catalog products, directory, and shipping profiles.</p>
              {showVerificationFlow ? (
                renderVendorOnboarding()
              ) : (
                <p>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowApply(true)}>
                    Apply for storefront
                  </button>
                </p>
              )}
            </section>
          );
        }
        return <VendorProductsPanel vendorUserId={sellerUserId} readOnly={readOnly} />;
      case 'shipping':
        return <VendorShippingPanel vendorUserId={sellerUserId} readOnly={readOnly} />;
      case 'claims':
        return <VendorClaimLinksPanel vendorUserId={sellerUserId} readOnly={readOnly} />;
      case 'orders':
        return <VendorOrdersPanel vendorUserId={sellerUserId} />;
      default:
        return null;
    }
  }

  const hint = caps?.canManageCatalog
    ? 'Manage your store, hosted checkout products, orders' +
      (marketplaceExtensions.showClaimLinksTab ? ', and community claim links' : '') +
      '.'
    : caps?.canListFinds
      ? 'Orders and payouts for finds listed from your haul reports.'
      : 'Accept marketplace terms and connect Stripe to start selling.';

  return (
    <div className="vendor-console feed-card settings-page">
      {suspended && (
        <div className="form-error" role="alert">
          Your seller account is {vendorState?.vendorStatus}. Hosted checkout and product publishing are disabled.
          {vendorState?.vendorSuspensionReason ? ` Reason: ${vendorState.vendorSuspensionReason}` : ''}
        </div>
      )}
      {needsTermsReaccept && <MarketplaceSellerTermsReaccept onAccepted={() => void refresh()} />}
      <ConnectNotificationBanner sellerUserId={sellerUserId} />
      <p className="vendor-console__hint">{hint}</p>
      {(caps?.canManageCatalog || caps?.canListFinds) && (
        <p>
          <Link href={`/marketplace/sellers/${sellerUserId}/`} className="btn btn-ghost">
            View public store
          </Link>
        </p>
      )}
      <nav className="vendor-console__tabs" aria-label="Seller sections">
        {sellerTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={active === tab.id ? 'vendor-console__tab vendor-console__tab--active' : 'vendor-console__tab'}
            onClick={() => setActive(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>
      <div className="vendor-console__panel">{renderPanel(active)}</div>
      <SellerPauseSelling caps={caps} onPaused={() => void refresh()} />
    </div>
  );
}
